import React, { useState } from "react";
import { Checkbox, Popover, makeStyles } from "@material-ui/core";
import moment from "moment-timezone";
import { HelpTip, POLICY_IDS, cap, humanizeLabel, humanizePolicy, pal } from "./reviewUi";

// Inbox filters by violation (content label / Meta policy category) and by
// date. State lives in the URL: ?label=a,b&policy=x&date=7d, or from/to
// (YYYY-MM-DD, local) for a custom range. Presets resolve at request time.

// Content labels the cases list filters on, harmful first.
export const FILTER_LABELS = [
  "phishing",
  "illegal",
  "spam",
  "gambling",
  "lottery_or_chance_based_promotion",
  "uncertain",
  "ordinary_commercial_promotion",
  "benign",
];
const labelName = (l) => (l === "benign" ? "Looks legit" : humanizeLabel(l));
const policyName = (id) => cap(humanizePolicy(id));

const DATE_PRESETS = [
  { key: "", label: "Any time" },
  { key: "today", label: "Today" },
  { key: "24h", label: "Last 24 hours", n: 24, unit: "hours" },
  { key: "7d", label: "Last 7 days", n: 7, unit: "days" },
  { key: "30d", label: "Last 30 days", n: 30, unit: "days" },
];

const DAY = "YYYY-MM-DD";
const POLICY_ID = /^[a-z0-9_]{1,80}$/;
const csv = (v) => [...new Set(String(v || "").split(",").map((x) => x.trim()).filter(Boolean))];
const isDay = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v || "") && moment(v, DAY, true).isValid();

export const parseFilters = (params) => {
  const date = DATE_PRESETS.some((p) => p.key && p.key === params.get("date")) ? params.get("date") : "";
  const day = (k) => (!date && isDay(params.get(k)) ? params.get(k) : "");
  return {
    labels: csv(params.get("label")).filter((l) => FILTER_LABELS.includes(l)),
    policies: csv(params.get("policy")).filter((p) => POLICY_ID.test(p)),
    date,
    from: day("from"),
    to: day("to"),
  };
};

export const NO_FILTERS = { labels: [], policies: [], date: "", from: "", to: "" };
export const hasFilters = (f) => !!(f.labels.length || f.policies.length || f.date || f.from || f.to);

// URL params for a filter state (empty values are removed from the URL).
export const filterParams = (f) => ({
  label: f.labels.join(","),
  policy: f.policies.join(","),
  date: f.date,
  from: f.date ? "" : f.from,
  to: f.date ? "" : f.to,
});

// What the cases request gets, from the URL params above: label / policy
// (comma lists) and ISO from / to. Presets resolve now, so a reload stays relative.
export const filterQuery = ({ label, policy, date, from, to } = {}) => {
  const preset = DATE_PRESETS.find((p) => p.key && p.key === date);
  if (preset) {
    const start = preset.n ? moment().subtract(preset.n, preset.unit) : moment().startOf("day");
    return { label, policy, from: start.toISOString() };
  }
  return {
    label,
    policy,
    from: from ? moment(from, DAY).startOf("day").toISOString() : "",
    to: to ? moment(to, DAY).endOf("day").toISOString() : "",
  };
};

const shortDay = (d) => moment(d, DAY).format("D MMM");
const dateText = (f) => {
  if (f.date) return DATE_PRESETS.find((p) => p.key === f.date).label;
  if (f.from && f.to) return f.from === f.to ? shortDay(f.from) : `${shortDay(f.from)} – ${shortDay(f.to)}`;
  if (f.from) return `Since ${shortDay(f.from)}`;
  if (f.to) return `Until ${shortDay(f.to)}`;
  return "Any time";
};

const useStyles = makeStyles((theme) => {
  const c = pal(theme);
  const opt = {
    cursor: "pointer",
    color: c.hint,
    background: "none",
    border: 0,
    padding: 0,
    font: "inherit",
    "&:hover": { color: c.text2 },
  };
  return {
    row: { display: "flex", alignItems: "baseline", gap: 14, padding: "0 24px 10px", fontSize: 12 },
    trigger: { ...opt, "& i": { fontStyle: "normal", fontSize: 9, marginLeft: 4, verticalAlign: 1 } },
    on: { color: c.primary, fontWeight: 600, "&:hover": { color: c.primary } },
    clear: { ...opt, marginLeft: "auto", textDecoration: "underline dotted", textUnderlineOffset: "3px" },
    paper: { borderRadius: 8, border: `1px solid ${c.line2}`, boxShadow: "0 6px 24px rgba(0,0,0,.08)", marginTop: 6 },
    menu: { padding: "10px 0", minWidth: 240, maxHeight: 420, overflowY: "auto", fontSize: 12.5, color: c.text },
    head: { padding: "8px 14px 4px", fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: c.hint },
    note: { padding: "0 14px 4px", fontSize: 11.5, color: c.hint },
    item: {
      display: "flex",
      alignItems: "center",
      gap: 4,
      padding: "1px 14px 1px 8px",
      cursor: "pointer",
      "&:hover": { background: "rgba(10,71,76,.04)" },
      "& b": { marginLeft: "auto", paddingLeft: 16, fontWeight: 400, color: c.hint, fontVariantNumeric: "tabular-nums" },
    },
    check: { padding: 4, "& svg": { fontSize: 18 } },
    more: { ...opt, display: "block", margin: "8px 14px 0", textDecoration: "underline dotted", textUnderlineOffset: "3px" },
    preset: {
      ...opt,
      display: "block",
      width: "100%",
      textAlign: "left",
      padding: "6px 14px",
      color: c.text2,
      "&:hover": { background: "rgba(10,71,76,.04)", color: c.text },
    },
    presetOn: { color: c.primary, fontWeight: 600, "&:hover": { color: c.primary } },
    range: { display: "grid", gridTemplateColumns: "40px 1fr", alignItems: "center", gap: "6px 8px", padding: "4px 14px 4px 28px", color: c.hint },
    input: {
      font: "inherit",
      fontSize: 12.5,
      padding: "3px 6px",
      borderRadius: 6,
      border: `1px solid ${c.line}`,
      color: c.text,
      background: "#fff",
      outline: "none",
      "&:focus": { borderColor: c.primary },
    },
    apply: { ...opt, color: c.primary, fontWeight: 600, margin: "6px 14px 2px 28px", "&:disabled": { color: c.hint, cursor: "default", fontWeight: 400 } },
  };
});

// Options for one group: with facets, by open-item count (zero counts hidden unless showAll).
const optionsFor = (known, counts, selected, showAll, valid) => {
  const all = [...new Set([...known, ...Object.keys(counts || {}).filter(valid), ...selected])];
  const opts = all.map((value, i) => ({ value, i, n: counts ? Number(counts[value] || 0) : null }));
  if (counts) opts.sort((a, b) => b.n - a.n || a.i - b.i);
  const shown = opts.filter((o) => !counts || showAll || o.n > 0 || selected.includes(o.value));
  return { shown, hidden: opts.length - shown.length };
};

function Group({ title, options, selected, name, onToggle, empty }) {
  const s = useStyles();
  return (
    <>
      <div className={s.head}>{title}</div>
      {!options.length ? <div className={s.note}>{empty}</div> : null}
      {options.map((o) => (
        <label key={o.value} className={s.item}>
          <Checkbox
            size="small"
            color="primary"
            className={s.check}
            checked={selected.includes(o.value)}
            onChange={() => onToggle(o.value)}
          />
          <span>{name(o.value)}</span>
          {o.n != null ? <b>{o.n.toLocaleString("en-IN")}</b> : null}
        </label>
      ))}
    </>
  );
}

function ViolationMenu({ filters, facets, onChange }) {
  const s = useStyles();
  const [anchor, setAnchor] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const n = filters.labels.length + filters.policies.length;
  const labels = optionsFor(FILTER_LABELS, facets?.labels, filters.labels, showAll, (k) => FILTER_LABELS.includes(k));
  const policies = optionsFor(POLICY_IDS, facets?.policies, filters.policies, showAll, (k) => POLICY_ID.test(k));
  const toggle = (key) => (value) => {
    const list = filters[key];
    onChange({ ...filters, [key]: list.includes(value) ? list.filter((v) => v !== value) : [...list, value] });
  };
  return (
    <>
      <HelpTip help="Show only accounts with an item the checker gave this content label or put in this Meta policy category.">
        <button
          type="button"
          aria-haspopup="true"
          aria-expanded={!!anchor}
          className={`${s.trigger} ${n ? s.on : ""}`}
          onClick={(e) => setAnchor(e.currentTarget)}
        >
          {n ? `Violation · ${n}` : "Violation"}
          <i>▾</i>
        </button>
      </HelpTip>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ className: s.paper, elevation: 0 }}
      >
        <div className={s.menu} role="group" aria-label="Filter by violation">
          {facets ? <div className={s.note}>Counts are open items.</div> : null}
          <Group title="Content" options={labels.shown} selected={filters.labels} name={labelName} onToggle={toggle("labels")} empty="Nothing open." />
          <Group
            title="Meta policy category"
            options={policies.shown}
            selected={filters.policies}
            name={policyName}
            onToggle={toggle("policies")}
            empty="Nothing open."
          />
          {labels.hidden + policies.hidden > 0 ? (
            <button type="button" className={s.more} onClick={() => setShowAll(true)}>
              Show all ({labels.hidden + policies.hidden} with nothing open)
            </button>
          ) : null}
        </div>
      </Popover>
    </>
  );
}

function DateMenu({ filters, onChange }) {
  const s = useStyles();
  const [anchor, setAnchor] = useState(null);
  const custom = !filters.date && !!(filters.from || filters.to);
  const [range, setRange] = useState(null); // { from, to } while Custom range is open
  const open = (e) => {
    setRange(custom ? { from: filters.from, to: filters.to } : null);
    setAnchor(e.currentTarget);
  };
  const pick = (date) => {
    onChange({ ...filters, date, from: "", to: "" });
    setAnchor(null);
  };
  const today = moment().format(DAY);
  const bad = range && range.from && range.to && range.from > range.to;
  return (
    <>
      <HelpTip help="When the checker looked at the template, ad or profile change. An account is listed if any of its items falls in the range.">
        <button
          type="button"
          aria-haspopup="true"
          aria-expanded={!!anchor}
          className={`${s.trigger} ${filters.date || custom ? s.on : ""}`}
          onClick={open}
        >
          {dateText(filters)}
          <i>▾</i>
        </button>
      </HelpTip>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ className: s.paper, elevation: 0 }}
      >
        <div className={s.menu} style={{ minWidth: 220 }} role="group" aria-label="Filter by date">
          {DATE_PRESETS.map((p) => (
            <button
              key={p.key || "any"}
              type="button"
              className={`${s.preset} ${!range && !custom && filters.date === p.key ? s.presetOn : ""}`}
              onClick={() => pick(p.key)}
            >
              {p.label}
            </button>
          ))}
          <button
            type="button"
            className={`${s.preset} ${range || custom ? s.presetOn : ""}`}
            onClick={() => setRange(range || { from: filters.from || "", to: filters.to || "" })}
          >
            Custom range
          </button>
          {range ? (
            <>
              <div className={s.range}>
                <span>From</span>
                <input
                  type="date"
                  aria-label="From date"
                  className={s.input}
                  value={range.from}
                  max={range.to || today}
                  onChange={(e) => setRange({ ...range, from: e.target.value })}
                />
                <span>To</span>
                <input
                  type="date"
                  aria-label="To date"
                  className={s.input}
                  value={range.to}
                  min={range.from || undefined}
                  max={today}
                  onChange={(e) => setRange({ ...range, to: e.target.value })}
                />
              </div>
              <button
                type="button"
                className={s.apply}
                disabled={(!range.from && !range.to) || bad}
                onClick={() => {
                  onChange({ ...filters, date: "", from: range.from, to: range.to });
                  setAnchor(null);
                }}
              >
                {bad ? "From must be before To" : "Apply"}
              </button>
            </>
          ) : null}
        </div>
      </Popover>
    </>
  );
}

export default function Filters({ filters, facets, onChange }) {
  const s = useStyles();
  return (
    <div className={s.row}>
      <ViolationMenu filters={filters} facets={facets} onChange={onChange} />
      <DateMenu filters={filters} onChange={onChange} />
      {hasFilters(filters) ? (
        <button type="button" className={s.clear} onClick={() => onChange(NO_FILTERS)}>
          Clear
        </button>
      ) : null}
    </div>
  );
}
